import '../customers/CustomerDetails.css';
import { useTranslation } from 'react-i18next';
import { React, Fragment, useState, useContext, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../../services/AppContext';
import { ModalContext } from '../../modal/ModalContext';
import { DeleteConfirmationModal } from '../../modal/global/DeleteConfirmationModal';
import SubscriptionEditor from './SubscriptionEditor';
import MultiSelector from '../../components/global/MultiSelector';

const SubscriptionDetails = () => {
    const { t } = useTranslation();
    const { apiClient, user, notify } = useContext(AppContext);
    const { showModal } = useContext(ModalContext);
    const [subscription, setSubscription] = useState(null);
    const [tagData, setTagData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [removing, setRemoving] = useState(false);
    const { id } = useParams();
    const navigate = useNavigate();
    const apiUrl = window.env?.REACT_APP_API_URL || '';

    const fetchTags = async () => {
        if (user.role !== 'Admin') return;
        const data = await apiClient.getTags();
        if (!data.hasErrors) {
            setTagData(data);
        }
    };

    const fetchData = async () => {
        const data = await apiClient.getSubscriptionDetails(id);
        if (!data.hasErrors) {
            setSubscription(data);
        } else {
            notify(t('backend.error.not_found', { field: 'Subscription' }), 'error');
            navigate('/subscriptions');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
        fetchTags();
        // eslint-disable-next-line
    }, [id]);

    const formatDate = (dateString) => new Date(dateString).toISOString().split('T')[0];
    const formatDateTime = (dateString) => new Date(dateString).toLocaleString();

    const isAdmin = user.role === 'Admin';
    const isManager = user.role === 'Manager';
    const isCustomer = user.role === 'Customer';

    const handleEdit = async () => {
        const updated = await showModal(<SubscriptionEditor subscription={subscription} />);
        if (updated) fetchData();
    };

    const handleDelete = async () => {
        const shouldDelete = await showModal(<DeleteConfirmationModal subject={t('pages.subscriptions.headers.details')} />);
        if (shouldDelete) {
            setRemoving(true);
            await apiClient.deleteSubscription(subscription.id);
            setRemoving(false);
            notify(t('pages.subscriptions.notifications.deleted'), 'success');
            navigate('/subscriptions');
        }
    };

    const handleRemoveSpentClass = async (spentClassId) => {
        const shouldDelete = await showModal(<DeleteConfirmationModal subject={t('pages.subscriptions.headers.spentClasses')} />);
        if (shouldDelete) {
            setRemoving(true);
            await apiClient.cancelSpentClass({ subscriptionId: subscription.id, spentClassId });
            setRemoving(false);
            notify(t('pages.customers.notifications.scanRemoved'), 'success');
            fetchData();
        }
    };

    if (loading) return <div className="loading">{t('common.loading')}</div>;
    if (!subscription) return <div>{t('common.notAvailable')}</div>;

    const company = subscription.company || { displayName: '', logoUrl: '', defaultCurrency: { symbol: '' } };
    const qrUrl = subscription.qrUrl || '';
    const costCurrency = company.defaultCurrency?.symbol || '';

    // Customer-specific view
    if (isCustomer) {
        return (
            <div>
                <div className='subscription-ticket'>
                    <div className='ticket-container'>
                        {/* Left Side - QR Code */}
                        <div className='qr-section'>
                            {qrUrl && (
                                <img src={qrUrl} alt='QR Code' className='qr-image' />
                            )}
                        </div>

                        {/* Right Side - Subscription Info */}
                        <div className='info-section'>
                            {/* Company Info */}
                            <div className='company-info'>
                                <h2 className='company-name'>
                                    {company.displayName || t('common.companyName')}
                                </h2>
                                {company.logoUrl && (
                                    <div className='container-circle-small'><img src={`${apiUrl}${company.logoUrl}`} alt='Company Logo' className='company-logo' /></div>
                                )}
                            </div>

                            {/* Subscription Details */}
                            <div className='details'>
                                <div className='info-field'><span className='field-title'>{t('pages.subscriptions.fields.starts')}:</span> <span className='field-value'>{formatDate(subscription.starts)}</span></div>
                                <div className='info-field'><span className='field-title'>{t('pages.subscriptions.fields.expires')}:</span> <span className='field-value'>{formatDate(subscription.expires)}</span></div>
                                <div className='info-field'><span className='field-title'>{t('pages.subscriptions.fields.left')}:</span> <span className='field-value'>{subscription.classesLeft}</span></div>
                                <div className='info-field'><span className='field-title'>{t('pages.subscriptions.fields.total')}:</span> <span className='field-value'>{subscription.total}</span></div>
                                {(isAdmin || isManager) && <div className='info-field'><span className='field-title'>{t('pages.subscriptions.fields.cost')}:</span> <span className='field-value'>{subscription.cost} {costCurrency}</span></div>}  
                                {subscription.discount > 0 && (
                                    <div className="info-field">
                                        <span className="field-title">{t('common.discount')}:</span>
                                        <span className="field-value">
                                            {`${Math.round(subscription.discount * 100)}% (${subscription.cost * subscription.discount} ${costCurrency})`}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Spent Classes for Customer */}
                    {subscription.spentClasses && subscription.spentClasses.length > 0 && (
                        <div className="ticket-container mg-t-2">
                            <div className="profile-card">
                                <div className="subscription-spent-classes-section">
                                    <div className="company-name">{t('pages.subscriptions.headers.spentClasses')}</div>
                                    <div className="spent-classes-list-scrollable">
                                        {subscription.spentClasses.map((spentClass) => (
                                            <div key={spentClass.id} className="spent-class-row">
                                                <span className="spent-class-manager">{spentClass.managerName}</span>
                                                <span className="spent-class-timestamp">{formatDateTime(spentClass.timestamp)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Admin/Manager view
    return (
        <div>
            {subscription && (
                <div>
                    <div className="customer-profile">
                        <div className="profile-card">
                            <div className="subscription-info">
                                <h3 className="profile-title">
                                    {t('pages.subscriptions.headers.details')}
                                </h3>
                                <div className="container-square">
                                    {qrUrl ? (
                                        <img src={qrUrl} alt="QR" />
                                    ) : (
                                        <div className="placeholder-picture">QR</div>
                                    )}
                                </div>
                                { isCustomer && (
                                <div className='company-info'>
                                        <h2 className='company-name'>
                                            {company.displayName || t('common.companyName')}
                                        </h2>
                                        {company.logoUrl && (
                                            <div className='container-circle-small'><img src={`${apiUrl}${company.logoUrl}`} alt='Company Logo' className='company-logo' /></div>
                                        )}
                                    </div>
                                )}
                                <div className="info-field"><span className="field-title">{t('pages.subscriptions.fields.owner')}:</span> <span className="field-value">{subscription.owner}</span></div>
                                <div className="info-field"><span className="field-title">{t('pages.subscriptions.fields.total')}:</span> <span className="field-value">{subscription.total}</span></div>
                                <div className="info-field"><span className="field-title">{t('pages.subscriptions.fields.left')}:</span> <span className="field-value">{subscription.classesLeft}</span></div>
                                {(isAdmin || isManager) && <div className="info-field"><span className="field-title">{t('pages.subscriptions.fields.cost')}:</span> <span className="field-value">{subscription.cost} {costCurrency}</span></div>}  
                                <div className="info-field"><span className="field-title">{t('pages.subscriptions.fields.starts')}:</span> <span className="field-value">{formatDate(subscription.starts)}</span></div>
                                <div className="info-field"><span className="field-title">{t('pages.subscriptions.fields.expires')}:</span> <span className="field-value">{formatDate(subscription.expires)}</span></div>
                                {(isAdmin || isManager) && (
                                    <>
                                        {subscription.discount > 0 && <div className="info-field"><span className="field-title">{t('pages.subscriptions.fields.discount')}:</span> <span className="field-value">{(subscription.discount * 100).toFixed(0)}% ({subscription.cost * subscription.discount} {costCurrency})</span></div>}
                                        {subscription.managersShare > 0 && <div className="info-field"><span className="field-title">{t('pages.subscriptions.fields.managersShare')}:</span> <span className="field-value">{subscription.managersShare}%</span></div>}
                                    </>
                                )}
                                {subscription.tags && subscription.tags.length > 0 && (
                                    <div className="mg-t-1">
                                        <MultiSelector
                                            options={tagData}
                                            selection={subscription.tags.map((t) => t.id)}
                                            onChange={() => { }}
                                            isReadOnly={true}
                                        />
                                    </div>
                                )}
                            </div>

                            {isAdmin && (
                                <div className="profile-actions">
                                    <button className="background-warning width-100p" onClick={handleEdit} disabled={removing}>
                                        {t('pages.subscriptions.actions.edit')}
                                    </button>
                                    <button className="background-danger width-100p mg-l-1" onClick={handleDelete} disabled={removing}>
                                        {t('pages.subscriptions.actions.delete')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {(isAdmin || isManager) && (
                        <div className="customer-profile mg-t-2">
                            <div className="profile-card">
                                <div className="subscription-spent-classes-section">
                                    <h4 className="profile-title">{t('pages.subscriptions.headers.spentClasses')}</h4>
                                    <div className="spent-classes-list-scrollable">
                                        {subscription.spentClasses && subscription.spentClasses.length > 0 ? (
                                            subscription.spentClasses.map((spentClass) => (
                                                <div key={spentClass.id} className="spent-class-row">
                                                    <span className="spent-class-manager">{spentClass.managerName}</span>
                                                    <span className="spent-class-timestamp">{formatDateTime(spentClass.timestamp)}</span>
                                                    {isAdmin && (
                                                        <button className="remove-spent-class-btn" onClick={() => handleRemoveSpentClass(spentClass.id)} disabled={removing}>
                                                            {t('pages.customers.actions.remove')}
                                                        </button>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="mg-t-1">{t('common.notAvailable')}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SubscriptionDetails; 